import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  InputAdornment,
  TextField as MUITextField,
} from "@mui/material";
import FieldError from "../fieldError";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export interface InputProps {
  value?: string | number | null;
  name: string;
  label?: string;
  variant?: "filled" | "outlined" | "standard";
  type?: "text" | "password" | "number" | "email";
  step?: number;
  rows?: number;
  size?: "tiny" | "small" | "medium";
  isDisabled?: boolean;
  isRequired?: boolean;
  endAdornment?: JSX.Element;
  startAdornment?: JSX.Element;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  placeHolder?: string;
  style?: React.CSSProperties;
  textAlign?: "left" | "center" | "right";
  autoComplete?: string;
}

const TextField: React.FC<InputProps> = ({
  value,
  name,
  onChange,
  error,
  placeHolder,
  label = "",
  step = 1,
  rows = 1,
  variant = "outlined",
  type = "text",
  isDisabled = false,
  isRequired = false,
  endAdornment = null,
  startAdornment = null,
  size = "small",
  textAlign = "left",
  style = {},
  autoComplete,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const handleClickShowPassword = useCallback(
    () => setIsVisible((prev) => !prev),
    [],
  );

  const endIcon = useMemo(() => {
    if (type !== "password") {
      return endAdornment;
    }

    return (
      <IconButton
        aria-label={t("ui.toggle_password")}
        onClick={handleClickShowPassword}
        size="small"
      >
        {isVisible ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    );
  }, [endAdornment, handleClickShowPassword, isVisible, type, t]);

  return (
    <div style={style}>
      <MUITextField
        label={label}
        name={name}
        placeholder={placeHolder}
        value={value}
        variant={variant}
        type={type === "password" && isVisible ? "text" : type}
        disabled={isDisabled}
        required={isRequired}
        autoComplete={autoComplete}
        onChange={onChange}
        fullWidth
        {...(size === "tiny"
          ? {
              size: "small",
              sx: {
                "& .MuiInputBase-input": {
                  // iOS Safari zooms the page when a focused input renders
                  // under 16px, so the tiny size only applies from sm up.
                  fontSize: { xs: 16, sm: 15 },
                  height: { xs: 16, sm: 15 },
                  padding: 1,
                },
                "& .MuiFormLabel-root": {
                  marginTop: -1.5,
                  fontSize: 15,
                  height: 15,
                  padding: 1,
                },
              },
            }
          : {
              size,
            })}
        slotProps={{
          input: {
            inputProps: {
              step: step,
              style: { textAlign },
            },
            ...(endIcon
              ? {
                  endAdornment: (
                    <InputAdornment position="end">{endIcon}</InputAdornment>
                  ),
                }
              : {}),
            ...(startAdornment
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      {startAdornment}
                    </InputAdornment>
                  ),
                }
              : {}),
          },
        }}
        {...(rows > 1
          ? {
              multiline: true,
              rows,
            }
          : {})}
      />

      <FieldError error={error} />
    </div>
  );
};

export default TextField;
